import { SalaryInfo, SalaryType, CostConcept, CTCResult, AppConfig } from '../types';
import { CALCULATION_CONSTANTS } from './constants';
import { CustomRates, SalaryTypeKey } from '../services/customRatesService';

/**
 * Determines the salary type based on SMMLV threshold
 */
export function determineSalaryType(
  baseSalary: number,
  connectivityAllowance: number,
  smmlv: number,
  integralMultiplier: number = CALCULATION_CONSTANTS.INTEGRAL_THRESHOLD_MULTIPLIER
): SalaryType {
  const totalSalary = baseSalary + connectivityAllowance;
  const threshold = smmlv * integralMultiplier;
  return totalSalary >= threshold ? 'integral' : 'ordinary';
}

/**
 * Calculates the base for prestaciones based on salary type
 */
export function calculateBase(
  baseSalary: number,
  connectivityAllowance: number,
  type: SalaryType
): number {
  const total = baseSalary + connectivityAllowance;
  return type === 'integral' 
    ? total * CALCULATION_CONSTANTS.INTEGRAL_CALCULATION_BASE 
    : total;
}

/**
 * Checks if salary is high (> 10 SMMLV)
 */
export function isHighSalary(baseSalary: number, smmlv: number, highSalaryMultiplier: number = CALCULATION_CONSTANTS.HIGH_SALARY_MULTIPLIER): boolean {
  return baseSalary > smmlv * highSalaryMultiplier;
}

/**
 * Main CTC calculation function - mirrors Excel RH sheet structure
 */
export function calculateCTC(
  baseSalary: number,
  connectivityAllowance: number,
  config: AppConfig,
  riskLevel: number = 0.522,
  salaryTypeOverride?: 'ordinario_inf' | 'ordinario_sup' | 'integral' | 'auto',
  operativeCosts?: {
    hiring: number;
    safetyStudy: number;
    laptopRental: number;
    deliverySupportCost: number;
  },
  additionalCosts?: {
    financing: number;
    managementFees: number;
  },
  customRates?: CustomRates
): CTCResult {
  const { smmlv, exchangeRate, gaPercentage, highSalaryMultiplier = 10, integralMultiplier = 13 } = config;
  
  // Determine salary type
  let salaryType: SalaryType;
  let applyHighSalaryRules = false;
  let effectiveSalaryType: SalaryTypeKey;
  
  if (salaryTypeOverride && salaryTypeOverride !== 'auto') {
    if (salaryTypeOverride === 'integral') {
      salaryType = 'integral';
      applyHighSalaryRules = true;
      effectiveSalaryType = 'integral';
    } else {
      salaryType = 'ordinary';
      applyHighSalaryRules = salaryTypeOverride === 'ordinario_sup';
      effectiveSalaryType = salaryTypeOverride as SalaryTypeKey;
    }
  } else {
    salaryType = determineSalaryType(baseSalary, connectivityAllowance, smmlv, integralMultiplier);
    applyHighSalaryRules = isHighSalary(baseSalary, smmlv, highSalaryMultiplier);
    if (salaryType === 'integral') {
      effectiveSalaryType = 'integral';
    } else {
      effectiveSalaryType = applyHighSalaryRules ? 'ordinario_sup' : 'ordinario_inf';
    }
  }
  
  const isIntegral = salaryType === 'integral';
  const baseCalculo = baseSalary + connectivityAllowance;
  const baseIntegral = baseCalculo * 0.70; // 70% for integral calculations
  
  const salaryInfo: SalaryInfo = {
    type: salaryType,
    baseSalary,
    connectivityAllowance,
    calculationBase: isIntegral ? baseIntegral : baseCalculo,
  };

  // Helper to get rate (custom or default)
  const getRate = (conceptName: string, defaultRate: number): number => {
    if (customRates && customRates[conceptName] !== undefined) {
      return customRates[conceptName];
    }
    return defaultRate;
  };

  const concepts: CostConcept[] = [];

  // ============================================
  // SECTION 1: BASE SALARY (Rows 9-10 in Excel)
  // ============================================
  concepts.push({
    name: 'Salary',
    category: 'base',
    percentage: 0,
    amountCOP: baseSalary,
    amountUSD: baseSalary / exchangeRate,
    formula: 'Salario base mensual',
    applies: true,
  });
  
  concepts.push({
    name: 'Connectivity assistance',
    category: 'base',
    percentage: 0,
    amountCOP: connectivityAllowance,
    amountUSD: connectivityAllowance / exchangeRate,
    formula: 'Auxilio de conectividad',
    applies: connectivityAllowance > 0,
  });

  // ============================================
  // SECTION 2: PRESTACIONES SOCIALES (Rows 11-14)
  // ============================================
  
  // Unemployment Fund (Cesantías) - Row 11
  const cesantiasRate = getRate('Unemployment Fund', CALCULATION_CONSTANTS.CESANTIAS_RATE);
  const cesantiasAmount = baseCalculo * cesantiasRate;
  concepts.push({
    name: 'Unemployment Fund',
    category: 'prestaciones',
    percentage: cesantiasRate,
    amountCOP: cesantiasAmount,
    amountUSD: cesantiasAmount / exchangeRate,
    formula: `(Salario + Auxilio) * ${(cesantiasRate * 100).toFixed(2)}%`,
    applies: true,
  });
  
  // Interest Unemployment Fund (Intereses Cesantías) - Row 12
  const interesesRate = getRate('Interest Unemployment Fund', CALCULATION_CONSTANTS.INTERESES_CESANTIAS_RATE);
  const interesesAmount = baseCalculo * interesesRate;
  concepts.push({
    name: 'Interest Unemployment Fund',
    category: 'prestaciones',
    percentage: interesesRate,
    amountCOP: interesesAmount,
    amountUSD: interesesAmount / exchangeRate,
    formula: `(Salario + Auxilio) * ${(interesesRate * 100).toFixed(2)}%`,
    applies: true,
  });
  
  // Vacation - Row 13
  const vacacionesRate = getRate('Vacation', CALCULATION_CONSTANTS.VACACIONES_RATE);
  const vacacionesBase = isIntegral ? baseIntegral : baseCalculo;
  const vacacionesAmount = vacacionesBase * vacacionesRate;
  concepts.push({
    name: 'Vacation',
    category: 'prestaciones',
    percentage: vacacionesRate,
    amountCOP: vacacionesAmount,
    amountUSD: vacacionesAmount / exchangeRate,
    formula: isIntegral 
      ? `(Salario + Auxilio) * 70% * ${(vacacionesRate * 100).toFixed(2)}%`
      : `(Salario + Auxilio) * ${(vacacionesRate * 100).toFixed(2)}%`,
    applies: true,
  });
  
  // Salary 13 (Prima) - Row 14
  const primaRate = getRate('Salary 13', CALCULATION_CONSTANTS.PRIMA_RATE);
  const primaAmount = baseCalculo * primaRate;
  concepts.push({
    name: 'Salary 13',
    category: 'prestaciones',
    percentage: primaRate,
    amountCOP: primaAmount,
    amountUSD: primaAmount / exchangeRate,
    formula: `(Salario + Auxilio) * ${(primaRate * 100).toFixed(2)}%`,
    applies: true,
  });


  // ============================================
  // SECTION 3: SEGURIDAD SOCIAL (Rows 15-17)
  // ============================================
  
  // Health System (Salud) - Row 15
  const saludApplies = applyHighSalaryRules || isIntegral;
  const saludRate = getRate('Health System', CALCULATION_CONSTANTS.SALUD_RATE);
  const saludBase = isIntegral ? baseIntegral : (baseSalary * 0.70);
  const saludAmount = saludApplies ? saludBase * saludRate : 0;
  concepts.push({
    name: 'Health System',
    category: 'seguridad',
    percentage: saludApplies ? saludRate : 0,
    amountCOP: saludAmount,
    amountUSD: saludAmount / exchangeRate,
    formula: saludApplies 
      ? `Salario * 70% * ${(saludRate * 100).toFixed(1)}%`
      : 'No aplica (salario <= 10 SMMLV)',
    applies: saludApplies,
  });
  
  // Retirement Fund (Pensión) - Row 16
  const pensionRate = getRate('Retirement Fund', CALCULATION_CONSTANTS.PENSION_RATE);
  const pensionBase = isIntegral ? baseIntegral : baseSalary;
  const pensionAmount = pensionBase * pensionRate;
  concepts.push({
    name: 'Retirement Fund',
    category: 'seguridad',
    percentage: pensionRate,
    amountCOP: pensionAmount,
    amountUSD: pensionAmount / exchangeRate,
    formula: isIntegral
      ? `(Salario + Auxilio) * 70% * ${(pensionRate * 100)}%`
      : `Salario * ${(pensionRate * 100)}%`,
    applies: true,
  });
  
  // Labor Risk Insurance (ARL) - Row 17
  const arlBase = isIntegral ? baseIntegral : baseSalary;
  const arlRate = getRate('Labor Risk Insurance', riskLevel / 100);
  const arlAmount = arlBase * arlRate;
  concepts.push({
    name: 'Labor Risk Insurance',
    category: 'seguridad',
    percentage: arlRate,
    amountCOP: arlAmount,
    amountUSD: arlAmount / exchangeRate,
    formula: isIntegral
      ? `(Salario + Auxilio) * 70% * ${(arlRate * 100).toFixed(3)}%`
      : `Salario * ${(arlRate * 100).toFixed(3)}%`,
    applies: true,
  });

  // ============================================
  // SECTION 4: PARAFISCALES (Rows 18-20)
  // ============================================
  
  // Caja de Compensación - Row 18
  const cajaRate = getRate('Contribucions by employer (Caja de compensacion Familiar)', CALCULATION_CONSTANTS.CAJA_COMPENSACION_RATE);
  const cajaBase = isIntegral ? baseIntegral : baseSalary;
  const cajaAmount = cajaBase * cajaRate;
  concepts.push({
    name: 'Contribucions by employer (Caja de compensacion Familiar)',
    category: 'parafiscales',
    percentage: cajaRate,
    amountCOP: cajaAmount,
    amountUSD: cajaAmount / exchangeRate,
    formula: isIntegral
      ? `(Salario + Auxilio) * 70% * ${(cajaRate * 100)}%`
      : `Salario * ${(cajaRate * 100)}%`,
    applies: true,
  });
  
  // SENA - Row 19
  const senaRate = getRate('Contribucions by employer (Sena)', CALCULATION_CONSTANTS.SENA_RATE);
  const senaApplies = applyHighSalaryRules || isIntegral;
  const senaBase = isIntegral ? baseIntegral : baseSalary;
  const senaAmount = senaApplies ? senaBase * senaRate : 0;
  concepts.push({
    name: 'Contribucions by employer (Sena)',
    category: 'parafiscales',
    percentage: senaApplies ? senaRate : 0,
    amountCOP: senaAmount,
    amountUSD: senaAmount / exchangeRate,
    formula: senaApplies 
      ? (isIntegral ? `(Salario + Auxilio) * 70% * ${(senaRate * 100)}%` : `Salario * ${(senaRate * 100)}%`)
      : 'No aplica',
    applies: senaApplies,
  });
  
  // ICBF - Row 20
  const icbfRate = getRate('Contribucions by employer (Icbf)', CALCULATION_CONSTANTS.ICBF_RATE);
  const icbfApplies = applyHighSalaryRules || isIntegral;
  const icbfBase = isIntegral ? baseIntegral : baseSalary;
  const icbfAmount = icbfApplies ? icbfBase * icbfRate : 0;
  concepts.push({
    name: 'Contribucions by employer (Icbf)',
    category: 'parafiscales',
    percentage: icbfApplies ? icbfRate : 0,
    amountCOP: icbfAmount,
    amountUSD: icbfAmount / exchangeRate,
    formula: icbfApplies 
      ? (isIntegral ? `(Salario + Auxilio) * 70% * ${(icbfRate * 100)}%` : `Salario * ${(icbfRate * 100)}%`)
      : 'No aplica',
    applies: icbfApplies,
  });


  // ============================================
  // SECTION 5: OTROS CONCEPTOS (Rows 21-23)
  // ============================================
  
  // Severance - Row 21
  const defaultSeveranceRate = isIntegral ? CALCULATION_CONSTANTS.SEVERANCE_INTEGRAL_RATE : (1/12);
  const severanceRate = getRate('Severance', defaultSeveranceRate);
  const severanceBase = isIntegral ? baseIntegral : baseSalary;
  const severanceAmount = severanceBase * severanceRate;
  concepts.push({
    name: 'Severance',
    category: 'beneficios',
    percentage: severanceRate,
    amountCOP: severanceAmount,
    amountUSD: severanceAmount / exchangeRate,
    formula: isIntegral
      ? `(Salario + Auxilio) * 70% * ${(severanceRate * 100).toFixed(2)}%`
      : `Salario * ${(severanceRate * 100).toFixed(2)}%`,
    applies: true,
  });
  
  // Life Insurance - Row 22
  const lifeInsuranceValue = getRate('Life insurance', CALCULATION_CONSTANTS.SEGURO_VIDA);
  concepts.push({
    name: 'Life insurance',
    category: 'beneficios',
    percentage: baseSalary > 0 ? lifeInsuranceValue / baseSalary : 0,
    amountCOP: lifeInsuranceValue,
    amountUSD: lifeInsuranceValue / exchangeRate,
    formula: `Valor fijo mensual: $${lifeInsuranceValue.toLocaleString()} COP`,
    applies: true,
  });
  
  // Medicina Prepagada - Row 23
  const medicinaPrepagadaValue = getRate('Medicina Prepagada', CALCULATION_CONSTANTS.MEDICINA_PREPAGADA);
  concepts.push({
    name: 'Medicina Prepagada',
    category: 'beneficios',
    percentage: baseSalary > 0 ? medicinaPrepagadaValue / baseSalary : 0,
    amountCOP: medicinaPrepagadaValue,
    amountUSD: medicinaPrepagadaValue / exchangeRate,
    formula: `Valor fijo mensual: $${medicinaPrepagadaValue.toLocaleString()} COP`,
    applies: true,
  });

  // ============================================
  // SUBTOTAL: COST OF LAW (Row 25)
  // ============================================
  const costOfLawCOP = concepts.reduce((sum, c) => sum + c.amountCOP, 0);
  concepts.push({
    name: 'Sub Total: Cost of law',
    category: 'costOfLaw',
    percentage: 0,
    amountCOP: costOfLawCOP,
    amountUSD: costOfLawCOP / exchangeRate,
    formula: 'SUM(Salary : Medicina Prepagada)',
    applies: true,
    isSubtotal: true,
  });

  // ============================================
  // SECTION 6: COSTOS OPERATIVOS (Rows 26-29)
  // ============================================
  const hiring = getRate('Hiring', operativeCosts?.hiring || 0);
  const safetyStudy = getRate('Safety Study', operativeCosts?.safetyStudy || 0);
  const laptopRental = getRate('Laptop Rental', operativeCosts?.laptopRental || 0);
  const deliverySupportCost = getRate('Delivery support cost', (operativeCosts?.deliverySupportCost || 0) / 100) * 100;
  
  // Hiring - Row 26 (value in USD)
  concepts.push({
    name: 'Hiring',
    category: 'operativos',
    percentage: 0,
    amountCOP: hiring * exchangeRate,
    amountUSD: hiring,
    formula: 'Costo de contratación',
    applies: hiring > 0,
  });
  
  // Safety Study - Row 27 (value in USD)
  concepts.push({
    name: 'Safety Study',
    category: 'operativos',
    percentage: 0,
    amountCOP: safetyStudy * exchangeRate,
    amountUSD: safetyStudy,
    formula: 'Estudio de seguridad',
    applies: safetyStudy > 0,
  });
  
  // Laptop Rental - Row 28 (value in USD)
  concepts.push({
    name: 'Laptop Rental',
    category: 'operativos',
    percentage: 0,
    amountCOP: laptopRental * exchangeRate,
    amountUSD: laptopRental,
    formula: 'Arriendo de equipo',
    applies: laptopRental > 0,
  });
  
  // Delivery Support Cost - Row 29 (percentage of Cost of Law)
  const deliverySupportAmount = deliverySupportCost > 0 ? costOfLawCOP * (deliverySupportCost / 100) : 0;
  concepts.push({
    name: 'Delivery support cost',
    category: 'operativos',
    percentage: deliverySupportCost / 100,
    amountCOP: deliverySupportAmount,
    amountUSD: deliverySupportAmount / exchangeRate,
    formula: deliverySupportCost > 0 ? `Sub Total Cost of law * ${deliverySupportCost}%` : 'No aplica',
    applies: deliverySupportCost > 0,
  });


  // ============================================
  // SUBTOTAL COST (Row 30)
  // ============================================
  const operativosCOP = (hiring * exchangeRate) + (safetyStudy * exchangeRate) + 
                        (laptopRental * exchangeRate) + deliverySupportAmount;
  const subtotalCostCOP = costOfLawCOP + operativosCOP;
  
  concepts.push({
    name: 'Sub Total Cost',
    category: 'subtotalCost',
    percentage: 0,
    amountCOP: subtotalCostCOP,
    amountUSD: subtotalCostCOP / exchangeRate,
    formula: 'Sub Total Cost of law + Hiring + Safety Study + Laptop Rental + Delivery support',
    applies: true,
    isSubtotal: true,
  });

  // ============================================
  // SECTION 7: COSTOS ADICIONALES (Rows 31-33)
  // ============================================
  const financingRate = additionalCosts?.financing || 0;
  const managementFeesRate = additionalCosts?.managementFees || 0;
  
  // Financing - Row 31 (percentage of Sub Total Cost)
  const financingAmount = subtotalCostCOP * (financingRate / 100);
  concepts.push({
    name: 'Financing',
    category: 'adicionales',
    percentage: financingRate / 100,
    amountCOP: financingAmount,
    amountUSD: financingAmount / exchangeRate,
    formula: financingRate > 0 ? `Sub Total Cost * ${financingRate}%` : 'No aplica',
    applies: financingRate > 0,
  });
  
  // G&A Colombia - Row 32 (percentage of Sub Total Cost)
  const gaAmount = subtotalCostCOP * (gaPercentage / 100);
  concepts.push({
    name: 'G&A Colombia',
    category: 'adicionales',
    percentage: gaPercentage / 100,
    amountCOP: gaAmount,
    amountUSD: gaAmount / exchangeRate,
    formula: `Sub Total Cost * ${gaPercentage}%`,
    applies: true,
  });
  
  // Management Fees - Row 33 (percentage of Sub Total Cost)
  const managementFeesAmount = subtotalCostCOP * (managementFeesRate / 100);
  concepts.push({
    name: 'Management Fees',
    category: 'adicionales',
    percentage: managementFeesRate / 100,
    amountCOP: managementFeesAmount,
    amountUSD: managementFeesAmount / exchangeRate,
    formula: managementFeesRate > 0 ? `Sub Total Cost * ${managementFeesRate}%` : 'No aplica',
    applies: managementFeesRate > 0,
  });

  // ============================================
  // TOTAL COST (Row 34)
  // ============================================
  const adicionalesCOP = financingAmount + gaAmount + managementFeesAmount;
  const totalCostCOP = subtotalCostCOP + adicionalesCOP;
  
  concepts.push({
    name: 'Total Cost',
    category: 'totalCost',
    percentage: 0,
    amountCOP: totalCostCOP,
    amountUSD: totalCostCOP / exchangeRate,
    formula: 'Sub Total Cost + Financing + G&A Colombia + Management Fees',
    applies: true,
    isSubtotal: true,
  });

  // Calculate subtotals by category
  const subtotals = {
    base: baseSalary + connectivityAllowance,
    prestaciones: concepts.filter(c => c.category === 'prestaciones').reduce((sum, c) => sum + c.amountCOP, 0),
    seguridad: concepts.filter(c => c.category === 'seguridad').reduce((sum, c) => sum + c.amountCOP, 0),
    parafiscales: concepts.filter(c => c.category === 'parafiscales').reduce((sum, c) => sum + c.amountCOP, 0),
    beneficios: concepts.filter(c => c.category === 'beneficios').reduce((sum, c) => sum + c.amountCOP, 0),
    costOfLaw: costOfLawCOP,
    operativos: operativosCOP,
    subtotalCost: subtotalCostCOP,
    adicionales: adicionalesCOP,
    totalCost: totalCostCOP,
  };

  return {
    salaryInfo,
    effectiveSalaryType,
    concepts,
    subtotals,
    totalCOP: totalCostCOP,
    totalUSD: totalCostCOP / exchangeRate,
    timestamp: new Date(),
  };
}
